/**
 * Card renderer registry (simplified landing, WP3b).
 *
 * Cards are typed payloads produced by entity-skill tools. Core registers a
 * renderer for every card type in the shared contract; modules may override a
 * type for their own `moduleId` (finance owns `strategy_list`, `strategy_detail`
 * and `comparison`). Renderers are lazy so a module's card bundle only loads
 * when one of its cards actually appears in a conversation.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { Card } from '@confluent/shared/lib/conversation-ui/cards';

export interface CardRendererProps<C extends Card = Card> {
  card: C;
  conversationId: string;
  disabled?: boolean;
}

export type CardRenderer = ComponentType<CardRendererProps>;

export type CardRendererLoader = () => Promise<{ default: CardRenderer }>;

const GENERIC = '*';

const loaders = new Map<string, CardRendererLoader>();
const resolved = new Map<string, LazyExoticComponent<CardRenderer>>();

function keyFor(cardType: string, moduleId?: string): string {
  return `${moduleId ?? GENERIC}::${cardType}`;
}

/**
 * Registers a renderer for `cardType`. Pass `moduleId` to register a renderer
 * that only applies to cards emitted by that module; omit it for the core
 * (generic) renderer used when no module-specific one exists.
 */
export function registerCardRenderer(cardType: string, loader: CardRendererLoader, moduleId?: string): void {
  const key = keyFor(cardType, moduleId);
  loaders.set(key, loader);
  resolved.delete(key);
}

/**
 * Resolves the renderer for a card. Module-specific registration wins over the
 * generic one. Returns `null` when nothing is registered for the type.
 */
export function resolveCardRenderer(cardType: string, moduleId?: string): LazyExoticComponent<CardRenderer> | null {
  const candidates = moduleId ? [keyFor(cardType, moduleId), keyFor(cardType)] : [keyFor(cardType)];
  for (const key of candidates) {
    const cached = resolved.get(key);
    if (cached) return cached;
    const loader = loaders.get(key);
    if (loader) {
      const component = lazy(loader);
      resolved.set(key, component);
      return component;
    }
  }
  return null;
}

/** Test-only: drops every registration so suites start from a clean registry. */
export function clearCardRegistryForTesting(): void {
  loaders.clear();
  resolved.clear();
}
